import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { CardPrintTemplate } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { upsertMaster, useCardPrintTemplates } from '../masters';
import { useSessions } from '../sessions-admin';
import { buildPostMapUrl, pickActiveTemplate, type CardPrintField } from './cardLayout';
import {
  getBadgeFieldPath,
  isOverlayOnlyField,
  isQrField,
  resolveCategoryBadgeStyle,
  withBadgeFieldPath,
} from './builtinFields';
import { applyCardPrintConfig, buildCardPrintConfigPayload } from './cardPrintSettings';
import {
  BOLD_STROKE_RATIO,
  buildFieldDisplayText,
  buildPostFieldLabelMap,
  computeFitFontSize,
  effectiveMaxLines,
  fitMode,
  LINE_HEIGHT,
  PT_TO_MM,
  TEXT_BOTTOM_MARGIN_MM,
  TEXT_TOP_INSET_MM,
} from './fieldText';
import { buildCardFontFaceCss, cardFontCss, hasEmbeddableBold } from './fontRegistry';
import { FontLicenseNotice } from './FontLicenseNotice';
import {
  DEFAULT_CARD_THEME,
  resolveCardTheme,
  themeToPayload,
  type ResolvedCardTheme,
} from './cardTheme';
import { svgToDataUrl } from './svgUtils';
import { ThemeEditor } from './ThemeEditor';
import { FieldsEditor } from './FieldsEditor';
import { JobSettingsPanel } from './JobSettingsPanel';
import { PostSelectionPanel } from './PostSelectionPanel';
import { FormatImportExport, type ImportSettingsResult } from './FormatImportExport';
import { QrCodeOverlay } from './QrCodeOverlay';
import { buildCardPdf, downloadPdf } from './buildCardPdf';
import { SlidePreview, SLIDE_W_PX, SLIDE_H_PX } from './SlidePreview';
import {
  buildAddableFields,
  buildInitialJobFields,
  buildPostQueue,
  defaultJobConfig,
  type CardPrintJobConfig,
} from './jobConfig';
import {
  collectCardPostBadgeValues,
  getCardPostBadgeLabel,
  useCardPrintPosts,
  type CardPrintPost,
} from './useCardPrintPosts';
import { useResolvedPhotoUrls } from './useResolvedPhotoUrls';
import { useCardCategoryNames } from './useCardCategoryNames';
import { useCardPrintParticipants } from './useCardPrintParticipants';

/**
 * カード印刷 (card-print) ページ (AW6 — old2 parity)。
 *
 * 3 ペイン構成:
 *   - 左: 投稿選択 (検索 / カテゴリチップ / AI絞込 / ページング 50/page / 全選択系)
 *   - 中: A4 preview (10 面付け、ResizeObserver 自動フィット or 固定 zoom、開始セル緑枠)
 *   - 右: タブ (項目・印刷 / テーマ / 共有)
 *       - 項目・印刷: 表示項目エディタ (add/remove/reorder) + adminNote + 開始セル + カットライン +
 *         埋め方 + 印刷ダイアログ / PDF ダウンロード
 *       - テーマ: テンプレ選択 + ResolvedCardTheme 編集 + プリセット + M7 保存 (master role)
 *       - 共有: フォーマット JSON 書出/読込
 *
 * v2 良差分は維持: M7 cardPrintTemplates master + テンプレ選択ドロップダウン +
 * ResizeObserver 自動フィット + QrCodeOverlay 実画像 + 権限ガード。
 *
 * 認可: ExportPage と同じ (master または該当 district admin)。
 */

const SHEET_W_MM = 210;
const SHEET_H_MM = 297;
const CARD_W_MM = 91;
const CARD_H_MM = 55;
const MARGIN_LEFT_MM = 14;
const MARGIN_TOP_MM = 11;
const COLS = 2;
const ROWS_PER_SHEET = 5;
const PER_SHEET = COLS * ROWS_PER_SHEET; // 10

const ZOOM_OPTIONS = [
  { label: '自動フィット', value: 0 },
  { label: '50%', value: 0.5 },
  { label: '75%', value: 0.75 },
  { label: '100% (実寸)', value: 1 },
];

type RightTab = 'fields' | 'theme' | 'io';

/** テンプレ選択ドロップダウンの「テンプレなし(自由レイアウト)」を表す sentinel。 */
const FORCE_NO_TEMPLATE = '__none__';

export function CardPrintPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const { state: authState } = useAdminAuth();
  const districtState = useDistrict(districtId ?? null);
  const postsState = useCardPrintPosts(districtId ?? null);
  const templatesState = useCardPrintTemplates();
  const sessionsState = useSessions(districtId ?? null);
  // セッション別絞り込みを「投稿者の所属セッション」で判定するための uid→joinedSessionIds map。
  const participantsState = useCardPrintParticipants(districtId ?? null);
  const { toast } = useToast();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [overrideTemplateId, setOverrideTemplateId] = useState<string | null>(null);
  const [themeDraft, setThemeDraft] = useState<ResolvedCardTheme>(DEFAULT_CARD_THEME);
  const [themeSaving, setThemeSaving] = useState(false);
  const [themeDirty, setThemeDirty] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{ done: number; total: number } | null>(null);
  const [pptxGenerating, setPptxGenerating] = useState(false);
  const [pptxProgress, setPptxProgress] = useState<{ done: number; total: number } | null>(null);
  const [activeTab, setActiveTab] = useState<RightTab>('fields');
  const [jobConfig, setJobConfig] = useState<CardPrintJobConfig>(defaultJobConfig);
  const [savingConfig, setSavingConfig] = useState(false);
  // 出力モード: カード印刷 (A4 名刺 PDF) か PowerPoint (16:9 / 1 投稿 1 スライド) か。
  const [outputMode, setOutputMode] = useState<'pdf' | 'pptx'>('pdf');
  // FieldsEditor を初期化した template id (template 切替で job fields を作り直す)
  const fieldsInitTemplateId = useRef<string | null>(null);
  // 地区保存済 cardPrintConfig をこの地区で適用済みか (初回ロード時のみ適用、以後の template 切替では使わない)
  const districtConfigAppliedRef = useRef<string | null>(null);

  const previewPaneRef = useRef<HTMLElement | null>(null);
  // プレビューペインの利用可能幅 (px)。モードごとに自然幅 (A4 or スライド) に対する倍率を算出する。
  const [paneUsableW, setPaneUsableW] = useState(600);
  const SHEET_W_PX = (SHEET_W_MM / 25.4) * 96;
  const SHEET_H_PX = (SHEET_H_MM / 25.4) * 96;
  useEffect(() => {
    const el = previewPaneRef.current;
    if (!el) return;
    const compute = (): void => {
      setPaneUsableW(Math.max(120, el.clientWidth - 16));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // 現在のモードの 1 枚あたり自然幅 (A4 縦 or 16:9 スライド)。
  const naturalW = outputMode === 'pptx' ? SLIDE_W_PX : SHEET_W_PX;
  const autoScale = Math.min(1, Math.max(0.2, paneUsableW / naturalW));
  // zoom===0 (自動) なら幅フィット、それ以外は固定倍率
  const previewScale = jobConfig.zoom === 0 ? autoScale : jobConfig.zoom;

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canPrint = isMaster || isDistrictAdminOfThis;

  const districtMode = districtState.status === 'ready' ? districtState.district.mode : null;

  const availableTemplates = useMemo<(CardPrintTemplate & { id: string })[]>(() => {
    if (templatesState.status !== 'ready') return [];
    return templatesState.items.filter((t) => t.archivedAt === null);
  }, [templatesState]);

  const activeTemplate = useMemo<(CardPrintTemplate & { id: string }) | null>(() => {
    if (templatesState.status !== 'ready' || !districtMode) return null;
    if (overrideTemplateId === FORCE_NO_TEMPLATE) return null; // 明示的にテンプレなし
    if (overrideTemplateId) {
      const found = availableTemplates.find((t) => t.id === overrideTemplateId);
      if (found) return found;
    }
    return pickActiveTemplate(availableTemplates, districtMode);
  }, [templatesState, districtMode, overrideTemplateId, availableTemplates]);

  const posts: CardPrintPost[] = postsState.status === 'ready' ? postsState.items : [];
  const selectedPosts = useMemo(
    () => posts.filter((p) => selectedIds.has(p.id)),
    [posts, selectedIds],
  );

  // セッション一覧 (useSessions が sortOrder 昇順 → 未設定は startAt 降順でソート済み) を左フィルタへ。
  const sessionOptions = useMemo(
    () =>
      sessionsState.status === 'ready'
        ? sessionsState.items.map((s) => ({ id: s.id, name: s.name }))
        : [],
    [sessionsState],
  );
  // post.sessionId → セッション名。カードの組み込み field `sessionName` の値解決 (preview / PDF 共通)。
  const sessionNameMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    if (sessionsState.status === 'ready') {
      for (const s of sessionsState.items) map[s.id] = s.name;
    }
    return map;
  }, [sessionsState]);

  // 写真 URL は選択中の投稿について解決 (preview + PDF + PowerPoint + 選択済リストのサムネ)。
  const resolvedPhotoUrls = useResolvedPhotoUrls(selectedPosts);

  // template / mode が確定した時に themeDraft / jobFields を初期化 (未保存編集は破棄)。
  // テンプレが無い (activeTemplate=null) 場合も組み込み既定 (mode 別) で初期化することで
  // 「テンプレートがなくても自由にレイアウト」できる。
  //
  // 地区初回ロード時に限り、地区保存済 `cardPrintConfig` があればそれを優先適用する
  // (表示項目+書式 / QRサイズ / 行間 / カットライン / 埋め方 / カテゴリ色)。template / district の
  // 両方が確定してから一度だけ適用し、以後ユーザーが template を切替えた場合はテンプレ既定で再初期化する。
  useEffect(() => {
    if (templatesState.status !== 'ready') return;
    if (districtState.status === 'loading') return; // 地区確定 (ready/error) まで待つ
    const firstForDistrict = districtConfigAppliedRef.current !== (districtId ?? null);
    if (activeTemplate) {
      const nextTheme = resolveCardTheme(activeTemplate.theme);
      // 同一地区でテンプレを切替えた場合は、ユーザーが指定した個別カテゴリ色を維持する
      // (テンプレ既定色の上に現行 categoryColors を merge)。地区初回はテンプレ既定にし、
      // この後の applyCardPrintConfig が保存済 categoryColors で上書きする。
      setThemeDraft((prev) =>
        firstForDistrict
          ? nextTheme
          : {
              ...nextTheme,
              categoryColors: { ...nextTheme.categoryColors, ...prev.categoryColors },
            },
      );
      setThemeDirty(false);
    }
    const initKey = activeTemplate ? activeTemplate.id : `__none__:${districtMode ?? '?'}`;
    // 同一 template への再初期化は抑止 (ただし地区初回は保存設定適用のため通す)。
    if (!firstForDistrict && fieldsInitTemplateId.current === initKey) return;
    fieldsInitTemplateId.current = initKey;

    const cfg =
      districtState.status === 'ready' ? districtState.district.cardPrintConfig : undefined;
    if (firstForDistrict) districtConfigAppliedRef.current = districtId ?? null;

    if (firstForDistrict && cfg) {
      // 地区保存済設定を適用 (fields は保存があれば優先、無ければテンプレ既定)。
      const applied = applyCardPrintConfig(cfg);
      setJobConfig((c) => ({
        ...c,
        fields: applied.fields ?? buildInitialJobFields(activeTemplate, districtMode),
        qrSize_mm: applied.qrSize_mm,
        fieldGap_mm: applied.fieldGap_mm,
        cropMarks: applied.cropMarks,
        fillBehavior: applied.fillBehavior,
      }));
      if (Object.keys(applied.categoryColors).length > 0) {
        setThemeDraft((t) => ({ ...t, categoryColors: { ...applied.categoryColors } }));
      }
    } else {
      setJobConfig((c) => ({ ...c, fields: buildInitialJobFields(activeTemplate, districtMode) }));
    }
  }, [activeTemplate?.id, districtMode, templatesState.status, districtState.status, districtId]);

  const theme = themeDraft;
  const jobFields = jobConfig.fields;

  // 地区の入力項目定義 (postFields)。バッジ対象項目セレクタ / ラベル解決 / 色候補に使う。
  const postFields = useMemo(
    () =>
      districtState.status === 'ready'
        ? (districtState.district.dataSettings?.postFields ?? [])
        : [],
    [districtState],
  );

  // データ設定 (postFields) の key→日本語ラベル。extraFields 項目のラベル表示に使う。
  const postFieldLabelMap = useMemo(() => buildPostFieldLabelMap(postFields), [postFields]);

  // カテゴリバッジの対象に選べる入力項目 = 選択式 (select / multiselect) の postField。
  const badgeFieldCandidates = useMemo(
    () =>
      postFields
        .filter((f) => f.type === 'select' || f.type === 'multiselect')
        .map((f) => ({ key: f.key, label: f.label || f.key })),
    [postFields],
  );

  // AI 分類コード→日本語名の解決元 settingsId 群 (地区の AI 設定 + 各投稿の解析設定)。
  const aiSettingsIds = useMemo<(string | null | undefined)[]>(() => {
    const ids: (string | null | undefined)[] = [];
    if (districtState.status === 'ready') ids.push(districtState.district.aiAnalysis?.settingsId);
    for (const p of posts) ids.push(p.aiAnalysis?.settingsId);
    return ids;
  }, [districtState, posts]);
  // code→日本語名マップ (M2 aiCategoryMasters 駆動、style.resolveCodes の項目で使用)。
  // loading は意図的に握りつぶす: マスタ確定前は codeToName={} で未ヒット=コード温存 (安全側)、
  // 確定後に日本語名へ再描画される。PDF は生成時 (click) に解決済みマップを同期で渡すため無影響。
  const { codeToName } = useCardCategoryNames(aiSettingsIds);

  const addableFields = useMemo(
    () => buildAddableFields(activeTemplate, posts, jobFields, postFieldLabelMap),
    [activeTemplate, posts, jobFields, postFieldLabelMap],
  );

  // 現在のバッジ対象項目 (バッジのスロットの fieldPath、既定 投稿タイプ)。絞り込みボタンも同じ項目で作る。
  const badgeFieldPath = getBadgeFieldPath(jobFields);

  // バッジ色の編集候補 = 対象項目の選択肢 (options 値) ∪ 投稿に実在する値 (昇順)。
  const categoryLabels = useMemo(() => {
    const selected = postFields.find((f) => f.key === badgeFieldPath);
    const optionValues = (selected?.options ?? [])
      .map((o) => o.value)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    const observed = collectCardPostBadgeValues(posts, badgeFieldPath);
    return [...new Set([...optionValues, ...observed])].sort();
  }, [postFields, badgeFieldPath, posts]);

  function handleBadgeFieldChange(fieldPath: string): void {
    setJobConfig((c) => ({ ...c, fields: withBadgeFieldPath(c.fields, fieldPath) }));
  }

  // selectedPosts を fillBehavior に応じてキュー化し、startIndex で先頭をパディング、PER_SHEET 単位に分割。
  const sheets = useMemo<(CardPrintPost | null)[][]>(() => {
    const queue = buildPostQueue(selectedPosts, jobConfig.fillBehavior, PER_SHEET);
    if (queue.length === 0) {
      return [Array.from({ length: PER_SHEET }, () => null)];
    }
    // 開始セルまで空セルでパディング (使い掛けラベル台紙対応)
    const cells: (CardPrintPost | null)[] = Array.from(
      { length: jobConfig.startIndex },
      () => null,
    );
    cells.push(...queue);
    const out: (CardPrintPost | null)[][] = [];
    for (let i = 0; i < cells.length; i += PER_SHEET) {
      const slice = cells.slice(i, i + PER_SHEET);
      const padded: (CardPrintPost | null)[] = [...slice];
      while (padded.length < PER_SHEET) padded.push(null);
      out.push(padded);
    }
    return out;
  }, [selectedPosts, jobConfig.fillBehavior, jobConfig.startIndex]);

  function handlePrint(): void {
    if (!canPrint) return;
    if (selectedPosts.length === 0) {
      toast('error', '印刷する投稿を選択してください');
      return;
    }
    if (jobFields.length === 0) {
      toast('error', '表示する項目を 1 つ以上追加してください');
      return;
    }
    window.print();
  }

  async function handleSaveTheme(): Promise<void> {
    if (!activeTemplate || !isMaster) return;
    setThemeSaving(true);
    try {
      const themePayload = themeToPayload(theme);
      await upsertMaster<CardPrintTemplate>({
        collectionName: 'cardPrintTemplates',
        id: activeTemplate.id,
        uid: authState.user?.uid ?? 'unknown-master',
        isNew: false,
        data: {
          mode: activeTemplate.mode,
          displayName: activeTemplate.displayName,
          layout: activeTemplate.layout,
          fields: activeTemplate.fields,
          theme: themePayload,
          enabled: activeTemplate.enabled,
          version: activeTemplate.version,
          schemaVersion: activeTemplate.schemaVersion,
          archivedAt: activeTemplate.archivedAt,
        },
      });
      toast('success', `テーマを保存しました (M7: ${activeTemplate.displayName})`);
      setThemeDirty(false);
    } catch (e) {
      toast('error', `テーマ保存失敗: ${(e as Error).message}`);
    } finally {
      setThemeSaving(false);
    }
  }

  function handleThemeChange(next: ResolvedCardTheme): void {
    setThemeDraft(next);
    setThemeDirty(true);
  }

  // 「共有」JSON 読込: テーマ (+ あれば印刷設定) を適用。
  function handleImportSettings(result: ImportSettingsResult): void {
    handleThemeChange(result.theme);
    if (result.job) {
      setJobConfig((c) => ({
        ...c,
        fields: result.job!.fields ?? c.fields,
        qrSize_mm: result.job!.qrSize_mm,
        fieldGap_mm: result.job!.fieldGap_mm,
        cropMarks: result.job!.cropMarks,
        fillBehavior: result.job!.fillBehavior,
      }));
    }
  }

  function handleResetFields(): void {
    setJobConfig((c) => ({ ...c, fields: buildInitialJobFields(activeTemplate, districtMode) }));
  }

  // 現在の印刷設計を「この地区の既定」として地区 doc に保存 (master または当該地区管理者)。
  async function handleSaveDistrictConfig(): Promise<void> {
    if (!canPrint || !districtId) return;
    setSavingConfig(true);
    try {
      const payload = buildCardPrintConfigPayload({
        fields: jobFields,
        categoryColors: theme.categoryColors,
        qrSize_mm: jobConfig.qrSize_mm,
        fieldGap_mm: jobConfig.fieldGap_mm,
        cropMarks: jobConfig.cropMarks,
        fillBehavior: jobConfig.fillBehavior,
      });
      await setDoc(
        doc(db, 'districts', districtId),
        {
          cardPrintConfig: payload,
          updatedAt: serverTimestamp(),
          updatedBy: authState.user?.uid ?? 'unknown',
        },
        { merge: true },
      );
      toast('success', 'この地区の印刷設定を保存しました');
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSavingConfig(false);
    }
  }

  // 地区保存済の印刷設定をクリア (テンプレ/既定に戻す)。
  async function handleClearDistrictConfig(): Promise<void> {
    if (!canPrint || !districtId) return;
    if (
      !window.confirm('この地区の保存済み印刷設定をクリアしますか? (テンプレート/既定に戻ります)')
    ) {
      return;
    }
    setSavingConfig(true);
    try {
      await setDoc(
        doc(db, 'districts', districtId),
        {
          cardPrintConfig: null,
          updatedAt: serverTimestamp(),
          updatedBy: authState.user?.uid ?? 'unknown',
        },
        { merge: true },
      );
      toast('success', '保存済み印刷設定をクリアしました');
    } catch (e) {
      toast('error', `クリアに失敗しました: ${(e as Error).message}`);
    } finally {
      setSavingConfig(false);
    }
  }

  async function handleDownloadPdf(): Promise<void> {
    if (!canPrint) return;
    if (selectedPosts.length === 0) {
      toast('error', '印刷する投稿を選択してください');
      return;
    }
    if (jobFields.length === 0) {
      toast('error', '表示する項目を 1 つ以上追加してください');
      return;
    }
    setPdfGenerating(true);
    setPdfProgress({ done: 0, total: selectedPosts.length });
    try {
      const photoUrls: Record<string, string | null> = {};
      const qrUrls: Record<string, string | null> = {};
      const hasQr = jobFields.some((f) => isQrField(f));
      for (const p of selectedPosts) {
        photoUrls[p.id] = resolvedPhotoUrls[p.id] ?? null;
        // QR は投稿位置を Google マップで開くためのもの (位置情報の無い投稿は null=QR なし)。
        qrUrls[p.id] = hasQr ? buildPostMapUrl(p) : null;
      }
      const pdf = await buildCardPdf({
        posts: selectedPosts,
        template: activeTemplate ?? undefined,
        theme,
        fields: jobFields,
        labelMap: postFieldLabelMap,
        codeToName,
        sessionNameMap,
        adminNote: jobConfig.adminNote,
        startIndex: jobConfig.startIndex,
        cropMarks: jobConfig.cropMarks,
        fillBehavior: jobConfig.fillBehavior,
        fieldGap_mm: jobConfig.fieldGap_mm,
        qrSize_mm: jobConfig.qrSize_mm,
        photoUrls,
        qrUrls,
        onProgress: (done, total) => setPdfProgress({ done, total }),
      });
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      downloadPdf(pdf, `cards_${districtId ?? 'district'}_${date}.pdf`);
      toast('success', `PDF を生成しました (${selectedPosts.length} 件)`);
    } catch (e) {
      toast('error', `PDF 生成失敗: ${(e as Error).message}`);
    } finally {
      setPdfGenerating(false);
      setPdfProgress(null);
    }
  }

  // PowerPoint (16:9 / 1 投稿 1 スライド) 書き出し。投稿選択・表示項目・テーマは PDF と共有し、
  // 出力だけをスライド形式に変える (面付け固有の startIndex/cropMarks/fillBehavior は非適用)。
  async function handleDownloadPptx(): Promise<void> {
    if (!canPrint) return;
    if (selectedPosts.length === 0) {
      toast('error', 'スライドにする投稿を選択してください');
      return;
    }
    if (jobFields.length === 0) {
      toast('error', '表示する項目を 1 つ以上追加してください');
      return;
    }
    setPptxGenerating(true);
    setPptxProgress({ done: 0, total: selectedPosts.length });
    try {
      const photoUrls: Record<string, string | null> = {};
      const qrUrls: Record<string, string | null> = {};
      const hasQr = jobFields.some((f) => isQrField(f));
      for (const p of selectedPosts) {
        photoUrls[p.id] = resolvedPhotoUrls[p.id] ?? null;
        qrUrls[p.id] = hasQr ? buildPostMapUrl(p) : null;
      }
      const districtName =
        districtState.status === 'ready' ? districtState.district.name : 'CitizenWalk';
      // pptxgenjs は重いため、PowerPoint 書き出し時のみ動的 import して別チャンクに分離する
      // (常時ロードされる管理画面メインバンドルに載せない)。
      const { buildCardPptx, downloadPptx } = await import('./buildCardPptx');
      const pptx = await buildCardPptx({
        posts: selectedPosts,
        theme,
        fields: jobFields,
        labelMap: postFieldLabelMap,
        codeToName,
        sessionNameMap,
        adminNote: jobConfig.adminNote,
        title: `${districtName} — 投稿スライド`,
        photoUrls,
        qrUrls,
        onProgress: (done, total) => setPptxProgress({ done, total }),
      });
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      await downloadPptx(pptx, `slides_${districtId ?? 'district'}_${date}.pptx`);
      toast('success', `PowerPoint を生成しました (${selectedPosts.length} 件)`);
    } catch (e) {
      toast('error', `PowerPoint 生成失敗: ${(e as Error).message}`);
    } finally {
      setPptxGenerating(false);
      setPptxProgress(null);
    }
  }

  const hasQrField = jobFields.some((f) => isQrField(f));

  return (
    <>
      <style>{`
        /* PDF と同一フォント (埋め込み可能な日本語フォント) を画面プレビューにも適用し折り返しを一致させる。
           font-weight は 400 のみ宣言 (太字は -webkit-text-stroke で擬似表現し字幅を Regular に保つ)。 */
        ${buildCardFontFaceCss()}
        @page { size: A4 portrait; margin: 0; }
        @media screen {
          .card-print-cell { outline: 0.1mm dashed #d4d4d4; }
          .card-print-cell-start { outline: 0.4mm solid #16a34a !important; }
        }
        @media print {
          body * { visibility: hidden; }
          .card-print-sheet, .card-print-sheet * { visibility: visible; }
          .card-print-sidebar { display: none !important; }
          .card-print-toolbar { display: none !important; }
          .card-print-sheet {
            position: absolute; left: 0; top: 0;
            width: ${SHEET_W_MM}mm; height: ${SHEET_H_MM}mm;
            page-break-after: always; margin: 0; box-shadow: none !important;
            transform: none !important;
          }
          .card-print-sheet:last-child { page-break-after: auto; }
          .card-print-cell, .card-print-cell-start { outline: none !important; }
          .card-print-sheet-wrap { width: auto !important; height: auto !important; margin: 0 !important; }
          .card-empty-ph { display: none !important; }
        }
      `}</style>

      <header className="mb-6 card-print-toolbar">
        <Link
          to={districtId ? `/districts/${districtId}/export` : '/districts'}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← エクスポート
        </Link>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
          <h1 className="text-2xl font-bold text-slate-900">
            {outputMode === 'pptx'
              ? '📊 PowerPoint 書き出し (16:9 / 1 枚 1 投稿)'
              : '🪪 カード印刷 (A4 / 名刺サイズ 10 面付け)'}
            {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
          </h1>
          <label className="flex items-center gap-1 text-xs text-slate-600">
            表示倍率
            <select
              value={jobConfig.zoom}
              onChange={(e) => setJobConfig((c) => ({ ...c, zoom: parseFloat(e.target.value) }))}
              aria-label="プレビュー表示倍率"
              className="text-xs border border-slate-300 rounded px-2 py-1 bg-white"
            >
              {ZOOM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* 出力形式の選択 (最初にどちらを作るか選ぶ)。プレビューもこの選択で切り替わる。 */}
        <div className="flex items-center flex-wrap gap-3 mt-3">
          <div
            role="radiogroup"
            aria-label="出力形式"
            className="inline-flex rounded-lg border border-slate-300 overflow-hidden"
          >
            {(
              [
                ['pdf', '🪪 カード印刷 (PDF)'],
                ['pptx', '📊 PowerPoint (16:9)'],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={outputMode === mode}
                onClick={() => setOutputMode(mode)}
                className={`px-3 py-1.5 text-sm font-medium ${
                  outputMode === mode
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-2">
          {outputMode === 'pptx'
            ? '選択した投稿を 16:9 の PowerPoint に、1 枚 1 投稿で書き出します。投稿の選択・記載項目・テーマはカード印刷と共通です。写真は全体が必ず収まるよう配置されます。'
            : '投稿を A4 名刺サイズ (55×91mm) 10 枚 / sheet で印刷します。PDF ダウンロードまたはブラウザの印刷ダイアログから出力できます。'}
        </p>
      </header>

      {!canPrint && (
        <Card padding="md" className="border-danger-600 mb-4 card-print-sidebar">
          <p className="text-sm text-danger-700">
            この地区のカード印刷権限がありません (role: {authState.role ?? 'none'}、地区:{' '}
            {districtId})。
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* 左ペイン: 投稿選択 */}
        <aside className="lg:col-span-3 min-w-0 card-print-sidebar">
          <Card padding="md" className="mb-3">
            <h2 className="text-sm font-bold text-slate-900 mb-2">投稿を選ぶ</h2>
            <PostSelectionPanel
              posts={posts}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
              loading={postsState.status === 'loading'}
              error={postsState.status === 'error' ? postsState.error : null}
              thumbUrls={resolvedPhotoUrls}
              canSelect={canPrint}
              sessions={sessionOptions}
              authorSessionsByUid={participantsState.authorSessionsByUid}
              badgeFieldPath={badgeFieldPath}
            />
          </Card>
        </aside>

        {/* 中央ペイン: preview (PDF=A4面付け / PowerPoint=16:9スライド) */}
        <main ref={previewPaneRef} className="lg:col-span-6 min-w-0 overflow-hidden">
          {outputMode === 'pptx' ? (
            selectedPosts.length === 0 ? (
              <div
                className="mx-auto mb-4 bg-white shadow-md flex items-center justify-center text-sm text-slate-400"
                style={{
                  width: `${SLIDE_W_PX * previewScale}px`,
                  height: `${SLIDE_H_PX * previewScale}px`,
                }}
              >
                左で投稿を選ぶと、1 枚 1 投稿のスライドが表示されます
              </div>
            ) : (
              selectedPosts.map((post, idx) => (
                <div
                  key={post.id}
                  className="mx-auto mb-4"
                  style={{
                    width: `${SLIDE_W_PX * previewScale}px`,
                    height: `${SLIDE_H_PX * previewScale}px`,
                  }}
                >
                  <div
                    className="shadow-md"
                    style={{
                      width: `${SLIDE_W_PX}px`,
                      height: `${SLIDE_H_PX}px`,
                      transform: `scale(${previewScale})`,
                      transformOrigin: 'top left',
                    }}
                  >
                    <div className="relative">
                      <span className="absolute -top-6 left-0 text-[11px] text-slate-400">
                        スライド {idx + 1} / {selectedPosts.length}
                      </span>
                      <SlidePreview
                        post={post}
                        fields={jobFields}
                        theme={theme}
                        adminNote={jobConfig.adminNote}
                        labelMap={postFieldLabelMap}
                        codeToName={codeToName}
                        sessionNameMap={sessionNameMap}
                        qrValue={hasQrField ? (buildPostMapUrl(post) ?? undefined) : undefined}
                        resolvedPhotoUrl={resolvedPhotoUrls[post.id]}
                      />
                    </div>
                  </div>
                </div>
              ))
            )
          ) : (
            sheets.map((sheet, sheetIdx) => (
              <div
                key={sheetIdx}
                className="card-print-sheet-wrap mx-auto mb-4"
                style={{
                  width: `${SHEET_W_PX * previewScale}px`,
                  height: `${SHEET_H_PX * previewScale}px`,
                }}
              >
                <div
                  className="card-print-sheet bg-white shadow-md"
                  style={{
                    width: `${SHEET_W_MM}mm`,
                    height: `${SHEET_H_MM}mm`,
                    position: 'relative',
                    overflow: 'hidden',
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  {sheet.map((post, cellIdx) => {
                    const col = cellIdx % COLS;
                    const row = Math.floor(cellIdx / COLS);
                    const x = MARGIN_LEFT_MM + col * CARD_W_MM;
                    const y = MARGIN_TOP_MM + row * CARD_H_MM;
                    const isStartCell = sheetIdx === 0 && cellIdx === jobConfig.startIndex;
                    return (
                      <div
                        key={cellIdx}
                        className={`card-print-cell${isStartCell ? ' card-print-cell-start' : ''}`}
                        style={{
                          position: 'absolute',
                          left: `${x}mm`,
                          top: `${y}mm`,
                          width: `${CARD_W_MM}mm`,
                          height: `${CARD_H_MM}mm`,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: theme.layout.photoSide === 'left' ? 'row' : 'row-reverse',
                          background: theme.backgroundColor,
                          // PDF と同じ選択フォント (埋め込み可能フォント) で描画し折り返し位置を一致させる。
                          fontFamily: cardFontCss(theme.fontFamily),
                          border:
                            theme.border.width_mm > 0
                              ? `${theme.border.width_mm}mm solid ${theme.border.color}`
                              : undefined,
                        }}
                      >
                        {post ? (
                          <CardCell
                            post={post}
                            fields={jobFields}
                            theme={theme}
                            adminNote={jobConfig.adminNote}
                            labelMap={postFieldLabelMap}
                            codeToName={codeToName}
                            sessionNameMap={sessionNameMap}
                            fieldGap_mm={jobConfig.fieldGap_mm}
                            qrValue={hasQrField ? (buildPostMapUrl(post) ?? undefined) : undefined}
                            qrSize_mm={jobConfig.qrSize_mm}
                            resolvedPhotoUrl={resolvedPhotoUrls[post.id]}
                          />
                        ) : (
                          <span style={{ fontSize: '8pt', color: '#cbd5e1', margin: 'auto' }}>
                            (空セル)
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </main>

        {/* 右ペイン: タブ (項目・印刷 / テーマ / 共有) */}
        <aside className="lg:col-span-3 min-w-0 card-print-sidebar relative z-10">
          <Card padding="md" className="mb-3">
            <div className="flex border-b border-slate-200 mb-3">
              {(
                [
                  ['fields', '📝 項目・印刷'],
                  ['theme', '🎨 テーマ'],
                  ['io', '📦 共有'],
                ] as const
              ).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-2 text-xs font-medium ${
                    activeTab === tab
                      ? 'text-primary-700 border-b-2 border-primary-600'
                      : 'text-slate-500'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {templatesState.status === 'loading' && (
              <p className="text-xs text-slate-500">テンプレ読込中…</p>
            )}
            {templatesState.status === 'error' && (
              <p className="text-xs text-danger-700">
                テンプレ読込エラー: {templatesState.error.message}
              </p>
            )}

            {templatesState.status === 'ready' && availableTemplates.length === 0 && (
              <p className="text-xs text-slate-500">
                印刷テンプレートはありません。組み込みカタログ (下の「項目・印刷」タブ) から
                投稿データ・AI 解析結果・地区固有データを選んで自由にレイアウトできます。
              </p>
            )}

            {activeTab === 'fields' && (
              <div className="grid grid-cols-1 gap-4">
                <FieldsEditor
                  jobFields={jobFields}
                  addableFields={addableFields}
                  onChange={(next: CardPrintField[]) =>
                    setJobConfig((c) => ({ ...c, fields: next }))
                  }
                  onReset={handleResetFields}
                  labelMap={postFieldLabelMap}
                  categoryColors={theme.categoryColors}
                  accentColor={theme.accent}
                  categoryLabels={categoryLabels}
                  onCategoryColorsChange={(next) =>
                    handleThemeChange({ ...theme, categoryColors: next })
                  }
                  badgeFieldCandidates={badgeFieldCandidates}
                  onBadgeFieldChange={handleBadgeFieldChange}
                  disabled={!canPrint}
                />
                <div className="pt-3 border-t border-slate-200">
                  <JobSettingsPanel
                    config={jobConfig}
                    onChange={setJobConfig}
                    disabled={!canPrint}
                    outputMode={outputMode}
                  />
                </div>
                <div className="pt-3 border-t border-slate-200">
                  <h3 className="text-sm font-bold text-slate-800 mb-1">この地区の既定</h3>
                  <p className="text-[10px] text-slate-500 mb-2">
                    現在の表示項目・書式・カテゴリ色・QRサイズ・余白などを、この地区の既定として保存します。
                    次回このページを開くと自動で読み込まれます (背景色・装飾はテーマ/共有JSON管轄)。
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleSaveDistrictConfig()}
                      disabled={!canPrint || savingConfig}
                      aria-label="この地区の既定として保存"
                    >
                      {savingConfig ? '保存中…' : '💾 この地区の既定として保存'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleClearDistrictConfig()}
                      disabled={!canPrint || savingConfig}
                      aria-label="保存済み設定をクリア"
                    >
                      クリア
                    </Button>
                  </div>
                </div>
                <div className="pt-3 border-t border-slate-200">
                  {outputMode === 'pptx' ? (
                    <>
                      <Button
                        type="button"
                        onClick={() => void handleDownloadPptx()}
                        disabled={
                          !canPrint ||
                          selectedPosts.length === 0 ||
                          jobFields.length === 0 ||
                          pptxGenerating
                        }
                        className="w-full"
                        aria-label="PowerPoint をダウンロード"
                      >
                        {pptxGenerating
                          ? `生成中… ${pptxProgress ? `${pptxProgress.done}/${pptxProgress.total}` : ''}`
                          : '📊 PowerPoint (.pptx) をダウンロード'}
                      </Button>
                      <p className="mt-2 text-[10px] text-slate-500">
                        選択 {selectedPosts.length} 件 = {selectedPosts.length} スライド (16:9 / 1
                        枚 1 投稿)
                      </p>
                      <p className="mt-1 text-[10px] text-slate-400">
                        文字は編集可能なテキストボックスで配置されます。写真は全体が必ず収まるよう
                        contain で配置し、余白は背景色で埋めます。
                      </p>
                    </>
                  ) : (
                    <>
                      <Button
                        type="button"
                        onClick={() => void handleDownloadPdf()}
                        disabled={
                          !canPrint ||
                          selectedPosts.length === 0 ||
                          jobFields.length === 0 ||
                          pdfGenerating
                        }
                        className="w-full"
                        aria-label="PDF をダウンロード"
                      >
                        {pdfGenerating
                          ? `生成中… ${pdfProgress ? `${pdfProgress.done}/${pdfProgress.total}` : ''}`
                          : '📄 PDF をダウンロード'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handlePrint}
                        disabled={!canPrint || selectedPosts.length === 0 || jobFields.length === 0}
                        className="w-full mt-2"
                      >
                        🖨 印刷ダイアログを開く
                      </Button>
                      <p className="mt-2 text-[10px] text-slate-500">
                        選択 {selectedPosts.length} 件 / 全 {sheets.length} シート (10 枚 / シート)
                      </p>
                      <p className="mt-1 text-[10px] text-slate-400">
                        PDF は日本語フォント (Noto Sans JP) を埋め込み、写真は contain
                        で焼き込みます。
                        印刷ダイアログ利用時は「余白なし」「背景画像を印刷」を有効にしてください。
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'theme' && (
              <div className="grid grid-cols-1 gap-3">
                {availableTemplates.length > 0 && (
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">
                      地区モード: <code>{districtMode ?? '(未取得)'}</code>
                    </label>
                    <select
                      value={overrideTemplateId ?? activeTemplate?.id ?? ''}
                      onChange={(e) => setOverrideTemplateId(e.target.value || null)}
                      aria-label="印刷テンプレート選択"
                      className="w-full text-xs border border-slate-300 rounded px-2 py-1"
                    >
                      <option value="">(mode {districtMode ?? '?'} の自動選択)</option>
                      <option value={FORCE_NO_TEMPLATE}>テンプレなし (自由レイアウト)</option>
                      {availableTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.displayName} ({t.mode})
                        </option>
                      ))}
                    </select>
                    {activeTemplate ? (
                      <p className="mt-2 text-[11px] text-slate-600">
                        使用中: <strong>{activeTemplate.displayName}</strong>{' '}
                        <span className="text-slate-400">
                          (mode <code>{activeTemplate.mode}</code> / 既定 fields{' '}
                          {activeTemplate.fields.length})
                        </span>
                      </p>
                    ) : (
                      <p className="mt-2 text-[11px] text-slate-600">
                        テンプレなし (自由レイアウト)。「項目・印刷」タブで投稿データ・AI
                        解析結果・地区固有データから項目を自由に追加できます。
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-900">テーマ</h2>
                  {isMaster && (
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      onClick={() => void handleSaveTheme()}
                      disabled={!activeTemplate || themeSaving || !themeDirty}
                      aria-label="テーマを M7 master に保存"
                    >
                      {themeSaving ? '保存中…' : themeDirty ? '保存 (M7)' : '保存済'}
                    </Button>
                  )}
                </div>
                {!isMaster && (
                  <p className="text-[10px] text-slate-500">
                    テーマの編集・永続化は master role のみ。preview には反映されます。
                  </p>
                )}
                {isMaster && (
                  <p className="text-[10px] text-slate-400">
                    保存は schema 対応分 (背景/メイン/本文色/フォント/ロゴ/装飾) のみ M7
                    に永続化されます。
                    副色・見出し色・カテゴリ色・レイアウト・枠線はプリセット由来でジョブ単位の見た目に反映されます。
                  </p>
                )}
                <ThemeEditor theme={theme} onChange={handleThemeChange} disabled={!canPrint} />
              </div>
            )}

            {activeTab === 'io' && (
              <FormatImportExport
                theme={theme}
                job={{
                  fields: jobFields,
                  qrSize_mm: jobConfig.qrSize_mm,
                  fieldGap_mm: jobConfig.fieldGap_mm,
                  cropMarks: jobConfig.cropMarks,
                  fillBehavior: jobConfig.fillBehavior,
                }}
                onImport={handleImportSettings}
                disabled={!canPrint}
              />
            )}
          </Card>
        </aside>
      </div>

      <FontLicenseNotice />
    </>
  );
}

interface CardCellProps {
  post: CardPrintPost;
  fields: CardPrintField[];
  theme: ResolvedCardTheme;
  adminNote: string;
  /** データ設定 postFields の key→日本語名 (showLabel 表示用)。 */
  labelMap: Record<string, string>;
  /** AI 分類コード→日本語名 (style.resolveCodes の項目で使用)。 */
  codeToName: Record<string, string>;
  /** post.sessionId → セッション名 (組み込み field sessionName 表示用)。PDF と一致させる。 */
  sessionNameMap: Record<string, string>;
  /** 表示項目どうしの行間 (mm)。PDF (buildCardPdf fieldGap_mm) と一致させる。 */
  fieldGap_mm: number;
  qrValue?: string;
  /** QR コードの一辺 (mm)。PDF (buildCardPdf qrSize_mm) と一致させる。 */
  qrSize_mm: number;
  resolvedPhotoUrl?: string | null;
}

function CardCell({
  post,
  fields,
  theme,
  adminNote,
  labelMap,
  codeToName,
  sessionNameMap,
  fieldGap_mm,
  qrValue,
  qrSize_mm,
  resolvedPhotoUrl,
}: CardCellProps): JSX.Element {
  // テキスト領域の幅 (mm) — 自動縮小/行数推定に使う (写真幅・左右パディングを除外)。
  const textAreaWidthMm = Math.max(
    8,
    CARD_W_MM - theme.layout.photoWidth_mm - theme.layout.textPadding_mm * 2,
  );
  const photoSrc =
    resolvedPhotoUrl !== undefined
      ? resolvedPhotoUrl
      : post.photoThumbPath && post.photoThumbPath.startsWith('http')
        ? post.photoThumbPath
        : post.photoPath && post.photoPath.startsWith('http')
          ? post.photoPath
          : null;
  const catField = fields.find((f) => f.key === 'category');
  const showCategory = !!catField;
  // バッジの対象項目はバッジのスロット (key='category') の fieldPath (既定 投稿タイプ)。PDF と一致。
  const catLabel = catField ? getCardPostBadgeLabel(post, catField.fieldPath) : '';
  const catColor = catLabel ? (theme.categoryColors[catLabel] ?? theme.accent) : theme.accent;
  // バッジのフォントサイズ・太字は category フィールドの書式 (style) を反映 (未設定は 7pt・太字)。PDF と一致。
  const badgeStyle = resolveCategoryBadgeStyle(catField);
  // 選択フォントが本物の Bold を持つか (持てば fontWeight 700、無ければ輪郭線で擬似太字)。PDF と一致。
  const hasRealBold = hasEmbeddableBold(theme.fontFamily);

  return (
    <>
      <div
        style={{
          width: `${theme.layout.photoWidth_mm}mm`,
          height: `${CARD_H_MM}mm`,
          flexShrink: 0,
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {photoSrc ? (
          <img
            src={photoSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: '8pt', color: '#94a3b8' }}>(写真なし)</span>
        )}
      </div>

      {/* category バッジ (左上) */}
      {showCategory && catLabel && (
        <span
          style={{
            position: 'absolute',
            // バッジは装飾 (template デザイン) より前面に出す。
            zIndex: 3,
            left: '1.2mm',
            top: '1.2mm',
            background: catColor,
            color: '#ffffff',
            fontSize: `${badgeStyle.fontSize}pt`,
            padding: '0.3mm 1.2mm',
            borderRadius: '0.6mm',
            // 太字: 本物の Bold があれば fontWeight 700、無ければ輪郭線で擬似 (PDF と同手法)。
            fontWeight: badgeStyle.bold && hasRealBold ? 700 : 400,
            WebkitTextStrokeWidth:
              badgeStyle.bold && !hasRealBold ? `${BOLD_STROKE_RATIO}em` : undefined,
            WebkitTextStrokeColor: badgeStyle.bold && !hasRealBold ? '#ffffff' : undefined,
            lineHeight: 1.1,
          }}
        >
          {catLabel}
        </span>
      )}

      {/* 装飾 SVG overlay */}
      {theme.decorations.map((d, idx) => {
        const tint =
          d.tintWith === 'secondary'
            ? theme.secondary
            : d.tintWith === 'accent' || d.tintWith === 'primary'
              ? theme.accent
              : null;
        const colored = tint ? d.svgSource.replace(/currentColor/gi, tint) : d.svgSource;
        const dataUrl = svgToDataUrl(colored);
        if (!dataUrl) return null;
        return (
          <img
            key={`deco-${idx}`}
            src={dataUrl}
            alt=""
            data-testid="card-decoration"
            data-slot={d.slot}
            style={{
              position: 'absolute',
              left: `${d.x_mm}mm`,
              top: `${d.y_mm}mm`,
              width: `${d.width_mm}mm`,
              height: `${d.height_mm}mm`,
              opacity: d.opacity ?? 1,
              transform: d.rotation_deg ? `rotate(${d.rotation_deg}deg)` : undefined,
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {qrValue && (
        <QrCodeOverlay
          value={qrValue}
          size_mm={qrSize_mm}
          x_mm={CARD_W_MM - qrSize_mm - 1.5}
          y_mm={CARD_H_MM - qrSize_mm - 1.5}
          darkColor={theme.textColor}
          lightColor={theme.backgroundColor}
        />
      )}

      {theme.logoUrl && /^https?:\/\//.test(theme.logoUrl) && (
        <img
          src={theme.logoUrl}
          alt=""
          style={{
            position: 'absolute',
            right: '2mm',
            bottom: '2mm',
            width: '12mm',
            height: '6mm',
            objectFit: 'contain',
            opacity: 0.8,
          }}
        />
      )}

      <div
        style={{
          flex: 1,
          // テキストは装飾 (template デザイン) より前面に出す (バッジと同じ z-index)。
          position: 'relative',
          zIndex: 3,
          // 上インセット / 下マージンは PDF (TEXT_TOP_INSET_MM / TEXT_BOTTOM_MARGIN_MM) と一致。
          padding: `${TEXT_TOP_INSET_MM}mm ${theme.layout.textPadding_mm}mm ${TEXT_BOTTOM_MARGIN_MM}mm`,
          display: 'flex',
          flexDirection: 'column',
          // 項目どうしの行間は PDF (buildCardPdf fieldGap) と一致させる。
          gap: `${fieldGap_mm}mm`,
          overflow: 'hidden',
        }}
      >
        {fields.length === 0 ? (
          <span style={{ fontSize: '7pt', color: '#cbd5e1' }}>(項目 未設定)</span>
        ) : (
          fields
            .filter((f) => !isOverlayOnlyField(f))
            .map((f) => {
              const isTitle = f.key === 'title';
              const text = buildFieldDisplayText(post, f, {
                adminNote,
                labelMap,
                codeToName,
                sessionNameMap,
              });
              const baseFontSize =
                f.style?.fontSize ??
                (isTitle ? theme.layout.titleFontSize_pt : theme.layout.bodyFontSize_pt);
              const bold = !!f.style?.bold || isTitle;
              const color = f.style?.color ?? (isTitle ? theme.textHeading : theme.textColor);
              const fit = fitMode(f);
              const maxLines = effectiveMaxLines(f, 2);
              // 空値ポリシー (PDF と一致): fit='fixed' は maxLines 行分の高さを確保 (整列維持)、
              // shrink/grow は行ごとスキップ (描画しない)。
              if (!text && fit !== 'fixed') return null;
              const fontSize = computeFitFontSize(
                text || ' ',
                baseFontSize,
                textAreaWidthMm,
                maxLines,
                fit,
              );
              // fixed: maxLines 行分の高さを常に確保 (カード間の整列が安定)。
              // shrink / grow: 自然高さ (最大 maxLines 行で clamp)。
              const fixedHeightMm =
                fit === 'fixed'
                  ? (fontSize * PT_TO_MM * LINE_HEIGHT * maxLines).toFixed(2)
                  : undefined;
              return (
                <div
                  key={f.key}
                  style={{
                    fontSize: `${fontSize}pt`,
                    // 太字: 本物の Bold があれば fontWeight 700 (実太字)、無ければ輪郭線で擬似
                    // (PDF の fillThenStroke と同じ比率・字幅 Regular のまま = 折り返し一致)。
                    fontWeight: bold && hasRealBold ? 700 : 400,
                    WebkitTextStrokeWidth:
                      bold && !hasRealBold ? `${BOLD_STROKE_RATIO}em` : undefined,
                    WebkitTextStrokeColor: bold && !hasRealBold ? color : undefined,
                    color,
                    lineHeight: LINE_HEIGHT,
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: maxLines,
                    overflow: 'hidden',
                    // 改行を保持 (PDF splitTextToSize の \n 強制改行と整合)。1 行固定時は nowrap。
                    whiteSpace: maxLines === 1 ? 'nowrap' : 'pre-wrap',
                    wordBreak: 'break-word',
                    textOverflow: 'ellipsis',
                    height: fixedHeightMm ? `${fixedHeightMm}mm` : undefined,
                    flexShrink: 0,
                  }}
                  title={text || f.label}
                >
                  {/* 空値プレースホルダは画面編集時のみ (印刷では非表示、PDF と一致)。 */}
                  {text || (
                    <span className="card-empty-ph" style={{ color: '#cbd5e1' }}>
                      {`(${f.label} 空)`}
                    </span>
                  )}
                </div>
              );
            })
        )}
      </div>
    </>
  );
}
